// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Nothing
// here ever leaves the device — the AI Closet Recap is a separate, scoped call (brain.ts).
//
// Data model (mirrors src-tauri/src/db.rs):
//   garment { item, brand, category, color, price_cents, purchased_on } + joined wear_count/last_worn_on
//   wear    { garment_id, worn_on }
//
// All deterministic math (cost-per-wear, dormancy, category balance, seasons) is
// computed in Rust (signals.rs); the helpers below are only formatting + tiny
// per-row presentation arithmetic for the list view.

import { invoke } from "./tauri-web";

export interface Garment {
  id: string;
  item: string;
  brand: string;
  category: string;
  color: string;
  /** Purchase price in minor units (cents). 0 when unknown. */
  price_cents: number;
  /** Date bought, "YYYY-MM-DD" local. "" when unknown. */
  purchased_on: string;
  created_at: string;
  updated_at: string;
  /** Data-URL of the garment photo (compressed JPEG), or "" when none. On-device only. */
  photo_ref: string;
  /** Joined: total logged wears for this garment. */
  wear_count: number;
  /** Joined: most recent wear date "YYYY-MM-DD", or "" if never worn. */
  last_worn_on: string;
}

export interface Wear {
  id: string;
  garment_id: string;
  worn_on: string;
  created_at: string;
}

export interface NewGarment {
  item: string;
  brand?: string;
  category?: string;
  color?: string;
  price_cents?: number;
  purchased_on?: string;
  photo_ref?: string;
}

export type GarmentPatch = Partial<{
  item: string;
  brand: string;
  category: string;
  color: string;
  price_cents: number;
  purchased_on: string;
  photo_ref: string;
}>;

// ── Derived signals (computed on-device in Rust; only aggregates are sent to the recap) ──
// Mirrors src-tauri/src/signals.rs::ClosetSignals. Every field is a computed aggregate —
// counts, money, days, ratios — never a raw per-wear row in bulk. This is the payload the
// AI recap NARRATES (see brain.ts): pure inventory facts, never advice.
export interface OverallSignals {
  total_items: number;
  total_wears: number;
  total_spend_cents: number;
  items_worn: number;
  items_never_worn: number;
  avg_cost_per_wear_cents: number | null;
  never_worn_spend_cents: number;
  value_deployed_ratio: number | null;
  first_purchase: string | null;
  last_wear: string | null;
  days_tracked: number;
  currency_note: string;
}
export interface ItemStat {
  id: string;
  item: string;
  brand: string;
  category: string;
  color: string;
  price_cents: number;
  purchased_on: string;
  wears: number;
  cost_per_wear_cents: number | null;
  days_since_purchase: number | null;
  last_worn_on: string;
  days_dormant: number | null;
}
export interface DormantItem {
  item: string;
  category: string;
  wears: number;
  last_worn_on: string;
  days_dormant: number | null;
}
export interface CategoryStat {
  category: string;
  items: number;
  wears: number;
  spend_cents: number;
  share_of_wears: number;
}
export interface SeasonStat {
  season: "winter" | "spring" | "summer" | "autumn";
  wears: number;
  share_of_wears: number;
}
export type FactTone = "good" | "watch" | "neutral";
export interface HeadlineFact {
  kind: "value" | "cost_per_wear" | "dormant" | "category" | "season" | "activity" | "milestone";
  text: string;
  tone: FactTone;
}
export interface ActivitySignals {
  wears_last_30d: number;
  wears_prev_30d: number;
  items_worn_last_30d: number;
  delta_30d: number;
  direction: "up" | "down" | "flat";
  most_worn_item: string | null;
  most_worn_count: number;
}
export interface ClosetSignals {
  schema: string;
  generated_for: string;
  overall: OverallSignals;
  items: ItemStat[];
  dormant: DormantItem[];
  categories: CategoryStat[];
  seasons: SeasonStat[];
  activity: ActivitySignals;
  headline_facts: HeadlineFact[];
}

// A saved outfit or packing list — the "plan & save" layer. garment_ids is a JSON array string.
export interface Collection {
  id: string;
  kind: "outfit" | "packing";
  name: string;
  garment_ids: string;
  occasion: string;
  /** ISO date (YYYY-MM-DD) this look is planned for, or "" when unplanned. */
  planned_on: string;
  created_at: string;
}
export interface NewCollection {
  kind?: "outfit" | "packing";
  name?: string;
  garment_ids: string; // JSON array of garment ids
  occasion?: string;
}
/** Parse a Collection's garment_ids JSON to a string[] (tolerant of bad data). */
export function collectionIds(c: Collection): string[] {
  try { const a = JSON.parse(c.garment_ids); return Array.isArray(a) ? a.filter((x) => typeof x === "string") : []; }
  catch { return []; }
}

export const store = {
  listGarments: (limit = 50000) => invoke<Garment[]>("list_garments", { limit }),
  getGarment: (id: string) => invoke<Garment | null>("get_garment", { id }),
  createGarment: (input: NewGarment) => invoke<Garment>("create_garment", { input }),
  updateGarment: (id: string, patch: GarmentPatch) => invoke<void>("update_garment", { id, patch }),
  deleteGarment: (id: string) => invoke<void>("delete_garment", { id }),

  logWear: (garmentId: string, wornOn?: string) =>
    invoke<Wear>("log_wear", { garmentId, wornOn: wornOn ?? null }),
  listWears: (garmentId: string, limit = 5000) =>
    invoke<Wear[]>("list_wears", { garmentId, limit }),
  deleteWear: (id: string) => invoke<void>("delete_wear", { id }),
  // Collections (saved outfits + packing lists)
  createCollection: (input: NewCollection) => invoke<Collection>("create_collection", { input }),
  listCollections: () => invoke<Collection[]>("list_collections"),
  /** Plan a saved look for a date (or "" to unplan). Pure date storage — no AI, no billing. */
  setCollectionPlan: (id: string, planned_on: string) => invoke<void>("set_collection_plan", { id, planned_on }),
  deleteCollection: (id: string) => invoke<void>("delete_collection", { id }),

  /**
   * On-device background removal for a garment photo (iOS Vision, iOS 17+).
   * Returns the ORIGINAL image with changed=false on older iOS, on web/macOS, or when nothing
   * subject-like was found — never throws, so a cutout failure can't block adding a garment.
   * The photo never leaves the device for this: no server, no cost.
   */
  removeBackground: async (image: string): Promise<{ image: string; changed: boolean }> => {
    try {
      const r = await invoke<{ image: string; changed: boolean }>("plugin:cutout|remove_background", { image });
      return { image: typeof r?.image === "string" && r.image ? r.image : image, changed: !!r?.changed };
    } catch {
      return { image, changed: false };
    }
  },

  /** Compute the derived-signals object on-device. `today` = client local YYYY-MM-DD so day/season math aligns to the user's clock. */
  computeSignals: (today: string) => invoke<ClosetSignals>("compute_signals", { today }),
};

// ── Pure presentation helpers (formatting only — the real math lives in Rust) ──

/** Curated garment categories; the user can also type their own. */
export const DEFAULT_CATEGORIES = [
  "tops", "bottoms", "dresses", "outerwear", "shoes",
  "knitwear", "activewear", "bags", "accessories", "jewellery",
] as const;

/** Curated colour suggestions; the user can also type their own. */
export const DEFAULT_COLORS = [
  "black", "white", "grey", "navy", "blue", "green",
  "red", "pink", "purple", "brown", "beige", "cream",
] as const;

/** Normalize a free-text field (category/colour) — lowercase, trimmed, whitespace→single space. */
export function normalizeLower(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Capitalise the first letter for display ("outerwear" → "Outerwear"). */
export function titleCase(s: string): string {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ── Money: stored as integer cents; the UI works in major units for input. ──
/** Parse a price string ("129.99") into integer cents. Returns 0 for blank/invalid. */
export function priceToCents(raw: string): number {
  const cleaned = raw.replace(/[^0-9.]/g, "").trim();
  if (!cleaned) return 0;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}
/** Format integer cents into a plain currency string. Symbol-agnostic (uses the locale separator). */
export function formatCents(cents: number, currency = "$"): string {
  const major = (cents / 100).toFixed(2);
  return `${currency}${major}`;
}
/** For an input field: integer cents → "129.99" (empty when 0). */
export function centsToInput(cents: number): string {
  return cents > 0 ? (cents / 100).toFixed(2) : "";
}

const toLocalDate = (iso: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};
export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/** Whole days between two YYYY-MM-DD strings (b - a). Null if either unparseable. */
export function daysBetween(a: string, b: string): number | null {
  const da = toLocalDate(a);
  const db = toLocalDate(b);
  if (!da || !db) return null;
  return Math.round((db.getTime() - da.getTime()) / 86_400_000);
}

/** Per-row cost-per-wear in cents for the list view (price / wears). Null when never worn. */
export function costPerWearCents(g: Garment): number | null {
  if (g.wear_count <= 0) return null;
  if (g.price_cents <= 0) return 0;
  return Math.round(g.price_cents / g.wear_count);
}

/** Days since the garment was last worn (today - last_worn_on). Null if never worn. */
export function daysDormant(g: Garment, today = todayLocal()): number | null {
  if (!g.last_worn_on) return null;
  return daysBetween(g.last_worn_on, today);
}

/** Plain, descriptive dormancy caption — NEVER prescriptive (no "wear it more"). */
export function dormancyCopy(g: Garment): string {
  if (g.wear_count === 0) return "Not worn yet";
  const d = daysDormant(g);
  if (d === null) return `Worn ${g.wear_count}×`;
  if (d === 0) return "Worn today";
  if (d === 1) return "Worn yesterday";
  if (d < 7) return `Worn ${d} days ago`;
  if (d < 30) return `Worn ${Math.round(d / 7)} ${Math.round(d / 7) === 1 ? "week" : "weeks"} ago`;
  if (d < 365) return `Worn ${Math.round(d / 30)} ${Math.round(d / 30) === 1 ? "month" : "months"} ago`;
  return `Worn over a year ago`;
}

/** Friendly "logged for" date label. */
export function fmtDate(s: string): string {
  return s
    ? new Date(s.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      })
    : "";
}
